<template>
  <a-drawer
    :open="open"
    :width="900"
    placement="right"
    :title="manifest?.type === 'theme' ? '主题审核详情' : '非组件资产审核详情'"
    @close="emit('update:open', false)"
  >
    <template v-if="review">
      <header class="review-head">
        <div>
          <h2>{{ review.package?.name ?? review.packageId }}</h2>
          <p>{{ typeLabel(manifest?.type) }} · 固定版本 v{{ review.release?.version ?? '-' }}</p>
        </div>
        <a-button type="primary" :loading="assessing" @click="runAssessment">
          <i class="ri-shield-check-line" />执行 / 重跑 Agent 初审
        </a-button>
      </header>

      <a-alert
        :type="manifest?.type === 'theme' ? 'success' : 'info'"
        show-icon
        :message="manifest?.type === 'theme' ? '预览仅渲染冻结主题版本的封面、场景配方与组件材质，不会修改任何账户的外观设置。' : '此页面审核冻结的非组件发布物，不提供组件 iframe 试用或组件运行时授权。最终通过或驳回仍由管理员在审核队列执行。'"
      />

      <a-tabs class="review-tabs">
        <a-tab-pane key="overview" tab="发布信息">
          <section class="facts">
            <div>
              <span>提交时间</span><strong>{{ date(review.submittedAt) }}</strong>
            </div>
            <div>
              <span>发布版本</span><strong>v{{ review.release?.version ?? '-' }}</strong>
            </div>
            <div>
              <span>产物摘要</span><code>{{ review.release?.artifactHash ?? '-' }}</code>
            </div>
          </section>
          <section class="section">
            <h3>更新说明</h3>
            <p>{{ review.release?.changelog || '未填写更新说明' }}</p>
          </section>
          <section class="section">
            <h3>冻结依赖</h3>
            <a-empty
              v-if="!review.release?.dependencies.length"
              :image="false"
              description="无依赖"
            />
            <a-descriptions v-else size="small" bordered :column="1">
              <a-descriptions-item
                v-for="item in review.release.dependencies"
                :key="item.alias"
                :label="item.alias"
              >
                {{ item.type }} · {{ item.packageId }} · {{ item.versionRange }}
                <template v-if="item.reason"> · {{ item.reason }}</template>
              </a-descriptions-item>
            </a-descriptions>
          </section>
        </a-tab-pane>

        <a-tab-pane key="manifest" tab="冻结内容">
          <a-empty v-if="!manifest" description="该审核记录缺少冻结发布物" />
          <template v-else-if="manifest.type === 'skill'">
            <section class="section">
              <h3>Skill 入口</h3>
              <a-descriptions bordered size="small" :column="1">
                <a-descriptions-item label="入口文件">{{ manifest.entry }}</a-descriptions-item>
                <a-descriptions-item label="入口类型">{{
                  manifest.entryKind ?? 'skill'
                }}</a-descriptions-item>
                <a-descriptions-item label="说明">{{ manifest.description }}</a-descriptions-item>
                <a-descriptions-item label="允许网络域名">{{
                  domains(manifest.network?.allowedDomains)
                }}</a-descriptions-item>
              </a-descriptions>
            </section>
            <section class="section">
              <h3>脚本</h3>
              <a-empty
                v-if="!manifest.scripts?.length"
                :image="false"
                description="未冻结可执行脚本"
              />
              <a-table
                v-else
                :data-source="skillScripts"
                :pagination="false"
                row-key="path"
                size="small"
              >
                <a-table-column title="路径" data-index="path" />
                <a-table-column title="运行时" data-index="runtime" />
                <a-table-column title="超时" data-index="timeout" />
                <a-table-column title="摘要" data-index="hash" />
              </a-table>
            </section>
            <section class="section">
              <h3>冻结文件</h3>
              <a-empty v-if="!manifest.files?.length" :image="false" description="未冻结文件内容" />
              <a-collapse v-else>
                <a-collapse-panel
                  v-for="file in manifest.files"
                  :key="file.path"
                  :header="file.path"
                >
                  <pre>{{ file.content }}</pre>
                </a-collapse-panel>
              </a-collapse>
            </section>
            <section v-if="manifest.source" class="section">
              <h3>来源记录</h3>
              <a-descriptions bordered size="small" :column="1">
                <a-descriptions-item label="来源类型">{{
                  manifest.source.kind
                }}</a-descriptions-item>
                <a-descriptions-item v-if="manifest.source.url" label="地址">{{
                  manifest.source.url
                }}</a-descriptions-item>
                <a-descriptions-item v-if="manifest.source.ref" label="引用">{{
                  manifest.source.ref
                }}</a-descriptions-item>
                <a-descriptions-item v-if="manifest.source.revision" label="修订">{{
                  manifest.source.revision
                }}</a-descriptions-item>
                <a-descriptions-item v-if="manifest.source.sourcePath" label="源路径">{{
                  manifest.source.sourcePath
                }}</a-descriptions-item>
                <a-descriptions-item v-if="manifest.source.filename" label="文件名">{{
                  manifest.source.filename
                }}</a-descriptions-item>
                <a-descriptions-item v-if="manifest.source.digest" label="摘要">{{
                  manifest.source.digest
                }}</a-descriptions-item>
              </a-descriptions>
            </section>
          </template>

          <template v-else-if="manifest.type === 'mcp'">
            <section class="section">
              <h3>MCP 启动信息</h3>
              <a-descriptions bordered size="small" :column="1">
                <a-descriptions-item label="传输方式">{{ manifest.transport }}</a-descriptions-item>
                <a-descriptions-item label="运行时">{{ manifest.runtime }}</a-descriptions-item>
                <a-descriptions-item label="入口文件">{{ manifest.entry }}</a-descriptions-item>
                <a-descriptions-item label="说明">{{ manifest.description }}</a-descriptions-item>
                <a-descriptions-item label="允许网络域名">{{
                  domains(manifest.network.allowedDomains)
                }}</a-descriptions-item>
              </a-descriptions>
            </section>
            <section class="section">
              <h3>Tools</h3>
              <a-empty v-if="!manifest.tools.length" :image="false" description="未声明 Tool" />
              <a-collapse v-else>
                <a-collapse-panel
                  v-for="tool in manifest.tools"
                  :key="tool.name"
                  :header="tool.name"
                >
                  <p>{{ tool.description }}</p>
                  <pre>{{ json(tool.inputSchema) }}</pre>
                </a-collapse-panel>
              </a-collapse>
            </section>
            <section v-if="manifest.configuration" class="section">
              <h3>配置要求</h3>
              <p>密钥字段：{{ manifest.configuration.secretFields?.join('、') || '无' }}</p>
              <p v-if="manifest.configuration.oauth">
                OAuth：{{ manifest.configuration.oauth.provider }} ·
                {{ manifest.configuration.oauth.scopes.join('、') }}
              </p>
              <pre>{{ json(manifest.configuration.schema) }}</pre>
            </section>
            <section class="section">
              <h3>冻结文件</h3>
              <a-collapse>
                <a-collapse-panel
                  v-for="file in manifest.files"
                  :key="file.path"
                  :header="file.path"
                >
                  <pre>{{ file.content }}</pre>
                </a-collapse-panel>
              </a-collapse>
            </section>
          </template>

          <template v-else-if="manifest.type === 'agent_template'">
            <section class="section">
              <h3>模板信息</h3>
              <a-descriptions bordered size="small" :column="1">
                <a-descriptions-item label="模板名称">{{ manifest.name }}</a-descriptions-item>
                <a-descriptions-item label="说明">{{ manifest.description }}</a-descriptions-item>
              </a-descriptions>
            </section>
            <section class="section">
              <h3>冻结 System Prompt</h3>
              <pre>{{ manifest.systemPrompt }}</pre>
            </section>
            <section class="section">
              <h3>推荐开场语</h3>
              <a-empty
                v-if="!manifest.starterPrompts?.length"
                :image="false"
                description="未设置开场语"
              />
              <a-list v-else :data-source="manifest.starterPrompts" bordered size="small">
                <template #renderItem="{ item }"
                  ><a-list-item>{{ item }}</a-list-item></template
                >
              </a-list>
            </section>
          </template>

          <template v-else-if="themeManifest">
            <section class="theme-review-grid">
              <div class="theme-live-preview" :style="themePreviewStyle">
                <header><strong>冻结主题材质</strong><a-segmented v-model:value="themeMode" size="small" :options="[{ label: '浅色', value: 'light' }, { label: '深色', value: 'dark' }]" /></header>
                <div class="theme-live-preview__body"><article><span>卡片材质</span><strong>审核工作台</strong><p>边框、高光、阴影与模糊均取自冻结 Release。</p><div><button type="button">次要操作</button><button type="button" class="primary">主要操作</button></div></article><label><span>输入控件</span><input readonly value="主题 token 预览" /></label></div>
              </div>
              <figure class="theme-cover-review"><img v-if="themeCover" :src="themeCover" :alt="`${review.package?.name ?? '主题'} 商店封面`" /><div v-else><i class="ri-image-line" />未上传商店封面</div><figcaption>商店封面</figcaption></figure>
            </section>
            <section class="section">
              <h3>主题配方</h3>
              <a-descriptions bordered size="small" :column="2"><a-descriptions-item label="场景素材">{{ themeManifest.assets.length }} 项</a-descriptions-item><a-descriptions-item label="截图">{{ review.release?.screenshots?.length ?? 0 }} 张</a-descriptions-item><a-descriptions-item label="浅色装饰">{{ themeManifest.light.decorations.length }} 个</a-descriptions-item><a-descriptions-item label="深色装饰">{{ themeManifest.dark?.decorations.length ?? 0 }} 个</a-descriptions-item><a-descriptions-item label="当前材质模糊">{{ themeVariant?.surfaces.card.blurPx ?? 0 }}px</a-descriptions-item><a-descriptions-item label="当前组件阴影">{{ themeVariant?.surfaces.card.shadow ?? 'none' }}</a-descriptions-item></a-descriptions>
            </section>
            <section v-if="review.release?.screenshots?.length" class="section"><h3>主题截图</h3><a-image-preview-group><div class="theme-screenshot-grid"><a-image v-for="(screenshot, index) in review.release.screenshots" :key="screenshot" :src="screenshot" :alt="`主题截图 ${index + 1}`" /></div></a-image-preview-group></section>
          </template>
        </a-tab-pane>

        <a-tab-pane key="assessment" tab="Agent 初审">
          <a-empty
            v-if="!assessment"
            description="初审尚未完成。新提交会自动触发；也可在此重跑初审。"
          />
          <template v-else>
            <section class="agent-summary">
              <h3>Agent 初审</h3>
              <a-tag :color="agentColor">{{ agentStatus }}</a-tag>
              <p>{{ assessment.agent?.summary ?? '未生成 Agent 报告。' }}</p>
              <p v-if="assessment.agent"><strong>初审结论：</strong>{{ initialDecision }}</p>
              <ul v-if="assessment.agent?.findings.length">
                <li v-for="item in assessment.agent.findings" :key="item">{{ item }}</li>
              </ul>
            </section>
            <section class="section">
              <h3>可核验检查项</h3>
              <a-list :data-source="assessment.staticFindings" size="small" bordered>
                <template #renderItem="{ item }"
                  ><a-list-item
                    ><a-tag :color="findingColor(item.level)">{{ findingLabel(item.level) }}</a-tag
                    >{{ item.message }}</a-list-item
                  ></template
                >
              </a-list>
            </section>
          </template>
        </a-tab-pane>

        <a-tab-pane key="tests" tab="审核测试记录">
          <a-form :model="testDraft" layout="vertical" @finish="saveTest">
            <div class="test-form">
              <a-form-item
                label="测试场景"
                name="scenario"
                :rules="[{ required: true, message: '请填写测试场景' }]"
                ><a-input
                  v-model:value="testDraft.scenario"
                  :maxlength="160"
                  placeholder="例如：入口文件与声明内容核验"
              /></a-form-item>
              <a-form-item label="结果" name="result"
                ><a-select v-model:value="testDraft.result" :options="testResultOptions"
              /></a-form-item>
            </div>
            <a-form-item
              label="证据"
              name="evidence"
              :rules="[{ required: true, message: '请记录观察到的结果或错误' }]"
              ><a-textarea
                v-model:value="testDraft.evidence"
                :maxlength="1000"
                :rows="3"
                show-count
                placeholder="记录核验步骤、发现的问题或阻断原因"
            /></a-form-item>
            <a-button html-type="submit" type="primary" :loading="savingTest"
              ><i class="ri-add-line" />记录测试结果</a-button
            >
          </a-form>
          <a-divider />
          <a-empty v-if="!testRecords.length" :image="false" description="还没有人工测试记录" />
          <a-list v-else :data-source="testRecords" bordered size="small">
            <template #renderItem="{ item }"
              ><a-list-item
                ><div class="test-record">
                  <div>
                    <strong>{{ item.scenario }}</strong
                    ><a-tag :color="testResultColor(item.result)">{{
                      testResultLabel(item.result)
                    }}</a-tag>
                  </div>
                  <p>{{ item.evidence }}</p>
                  <small>{{ date(item.createdAt) }} · {{ item.reviewerId }}</small>
                </div></a-list-item
              ></template
            >
          </a-list>
        </a-tab-pane>
      </a-tabs>
    </template>
  </a-drawer>
</template>

<script setup lang="ts">
import { computed, ref, watch, type CSSProperties } from 'vue';
import { message } from 'ant-design-vue';
import type {
  JsonSchema,
  MarketplaceReleaseManifest,
  MarketplaceReviewAssessment,
  MarketplaceReviewTestRecord,
  MarketplaceReviewView,
} from 'shared';
import { adminApi } from '@/api';

const props = defineProps<{ open: boolean; review?: MarketplaceReviewView }>();
const emit = defineEmits<{ 'update:open': [value: boolean]; changed: [] }>();
const assessment = ref<MarketplaceReviewAssessment | null>();
const assessing = ref(false);
const savingTest = ref(false);
const testRecords = ref<MarketplaceReviewTestRecord[]>([]);
const testDraft = ref<{
  scenario: string;
  result: MarketplaceReviewTestRecord['result'];
  evidence: string;
}>({ scenario: '', result: 'passed', evidence: '' });
const manifest = computed<Exclude<MarketplaceReleaseManifest, { type: 'component' }> | undefined>(
  () => {
    const value = props.review?.release?.manifest;
    return value?.type === 'component' ? undefined : value;
  },
);
const themeMode = ref<'light' | 'dark'>('light');
const themeManifest = computed(() => manifest.value?.type === 'theme' ? manifest.value : null);
const themeVariant = computed(() => {
  const theme = themeManifest.value;
  return theme ? themeMode.value === 'dark' ? (theme.dark ?? theme.light) : theme.light : null;
});
const themeCover = computed(() => props.review?.release?.coverImage || props.review?.release?.screenshots?.[0] || '');
const themePreviewStyle = computed<CSSProperties>(() => {
  const value = themeVariant.value;
  if (!value) return {};
  const material = (surface: keyof typeof value.surfaces) => {
    const recipe = value.surfaces[surface];
    return `color-mix(in srgb, ${recipe.tint} ${Math.round(recipe.opacity * 100)}%, transparent)`;
  };
  const card = value.surfaces.card;
  const control = value.surfaces.control;
  return {
    '--theme-canvas': value.palette.canvas,
    '--theme-text': value.palette.text,
    '--theme-muted': value.palette.textMuted,
    '--theme-primary': value.palette.primary,
    '--theme-scene': themeCover.value ? `url("${themeCover.value}")` : 'none',
    '--theme-panel': material('panel'),
    '--theme-card': material('card'),
    '--theme-control': material('control'),
    '--theme-border': `color-mix(in srgb, ${card.borderColor} ${Math.round(card.borderOpacity * 100)}%, transparent)`,
    '--theme-control-border': `color-mix(in srgb, ${control.borderColor} ${Math.round(control.borderOpacity * 100)}%, transparent)`,
    '--theme-highlight': `color-mix(in srgb, ${card.highlightColor} ${Math.round(card.highlightOpacity * 100)}%, transparent)`,
    '--theme-blur': `${card.blurPx}px`,
    '--theme-radius': `${card.radius}px`,
  } as CSSProperties;
});
const skillScripts = computed(() =>
  manifest.value?.type === 'skill'
    ? (manifest.value.scripts ?? []).map((item) => ({ ...item, timeout: `${item.timeoutMs}ms` }))
    : [],
);
const agentStatus = computed(
  () =>
    ({ ready: '初审完成', unavailable: '未配置模型', failed: '初审失败' })[
      assessment.value?.agent?.status ?? 'unavailable'
    ],
);
const agentColor = computed(
  () =>
    ({ ready: 'blue', unavailable: 'default', failed: 'red' })[
      assessment.value?.agent?.status ?? 'unavailable'
    ],
);
const initialDecision = computed(
  () =>
    ({
      pass: '通过初审，进入人工终审',
      block: '初审拦截，须优先核验问题',
      manual_review: '初审无法判断，转人工复核',
    })[assessment.value?.agent?.initialDecision ?? 'manual_review'],
);
const testResultOptions = [
  { value: 'passed', label: '通过' },
  { value: 'failed', label: '失败' },
  { value: 'blocked', label: '阻断' },
];

function date(value?: string | null): string {
  return value ? new Date(value).toLocaleString('zh-CN', { hour12: false }) : '-';
}
function typeLabel(type?: string): string {
  return (
    ({ skill: 'Skill', mcp: 'MCP', agent_template: 'Agent 模板' } as Record<string, string>)[
      type ?? ''
    ] ?? '非组件资产'
  );
}
function domains(items?: string[]): string {
  return items?.length ? items.join('、') : '未声明';
}
function json(value: JsonSchema): string {
  return JSON.stringify(value, null, 2);
}
function findingColor(level: string): string {
  return (
    ({ info: 'blue', warning: 'orange', high: 'red' } as Record<string, string>)[level] ?? 'default'
  );
}
function findingLabel(level: string): string {
  return (
    ({ info: '提示', warning: '注意', high: '高风险' } as Record<string, string>)[level] ?? level
  );
}
function testResultColor(result: string): string {
  return (
    ({ passed: 'green', failed: 'red', blocked: 'orange' } as Record<string, string>)[result] ??
    'default'
  );
}
function testResultLabel(result: string): string {
  return (
    ({ passed: '通过', failed: '失败', blocked: '阻断' } as Record<string, string>)[result] ??
    result
  );
}
async function loadAssessment(): Promise<void> {
  if (props.review)
    assessment.value = await adminApi.get<MarketplaceReviewAssessment | null>(
      `/admin/marketplace/reviews/${props.review.id}/assessment`,
    );
}
async function runAssessment(): Promise<void> {
  if (!props.review) return;
  assessing.value = true;
  try {
    assessment.value = await adminApi.post<MarketplaceReviewAssessment>(
      `/admin/marketplace/reviews/${props.review.id}/assessment`,
      {},
    );
    message.success('Agent 初审已完成');
    emit('changed');
  } catch (error) {
    message.error(error instanceof Error ? error.message : '检查执行失败');
  } finally {
    assessing.value = false;
  }
}
async function saveTest(): Promise<void> {
  if (!props.review) return;
  savingTest.value = true;
  try {
    const record = await adminApi.post<MarketplaceReviewTestRecord>(
      `/admin/marketplace/reviews/${props.review.id}/test-records`,
      testDraft.value,
    );
    testRecords.value = [record, ...testRecords.value];
    testDraft.value = { scenario: '', result: 'passed', evidence: '' };
    message.success('测试记录已保存');
    emit('changed');
  } catch (error) {
    message.error(error instanceof Error ? error.message : '测试记录保存失败');
  } finally {
    savingTest.value = false;
  }
}
watch(
  () => [props.open, props.review?.id] as const,
  async ([open, reviewId]) => {
    if (!open || !reviewId) return;
    assessment.value = props.review?.assessment ?? null;
    testRecords.value = props.review?.testRecords ? [...props.review.testRecords] : [];
    try {
      await loadAssessment();
    } catch {
      message.error('无法加载初审结果');
    }
  },
);
</script>

<style scoped>
.review-head {
  display: flex;
  justify-content: space-between;
  gap: 20px;
  align-items: start;
  margin-bottom: 18px;
}
.review-head h2,
.review-head p,
.section p {
  margin: 0;
}
.review-head p,
.section p,
.test-record p,
.test-record small {
  color: var(--aw-text-secondary);
}
.review-tabs {
  margin-top: 18px;
}
.facts {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}
.facts div,
.agent-summary {
  padding: 14px;
  border: 1px solid var(--aw-border);
  border-radius: var(--aw-radius-sm);
}
.facts span {
  display: block;
  margin-bottom: 6px;
  color: var(--aw-text-secondary);
  font-size: 12px;
}
.facts code {
  display: block;
  overflow-wrap: anywhere;
}
.section {
  margin-top: 20px;
}
.section h3,
.agent-summary h3 {
  margin: 0 0 10px;
  font-size: 14px;
}
.theme-review-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(240px, 0.8fr);
  gap: 14px;
}
.theme-live-preview {
  overflow: hidden;
  border: 1px solid var(--theme-border);
  border-radius: var(--theme-radius);
  color: var(--theme-text);
  background-color: var(--theme-canvas);
  background-image: linear-gradient(color-mix(in srgb, var(--theme-panel) 78%, transparent), color-mix(in srgb, var(--theme-panel) 78%, transparent)), var(--theme-scene);
  background-position: center;
  background-size: cover;
  backdrop-filter: blur(var(--theme-blur)) saturate(1.08);
}
.theme-live-preview header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  border-bottom: 1px solid var(--theme-border);
  color: var(--theme-muted);
}
.theme-live-preview__body {
  display: grid;
  gap: 10px;
  padding: 12px;
  background: color-mix(in srgb, var(--theme-canvas) 34%, transparent);
}
.theme-live-preview__body article {
  padding: 13px;
  border: 1px solid var(--theme-border);
  border-radius: calc(var(--theme-radius) * 0.84);
  background: var(--theme-card);
  box-shadow: inset 0 1px 0 var(--theme-highlight);
}
.theme-live-preview__body span {
  display: block;
  color: var(--theme-muted);
  font-size: 12px;
}
.theme-live-preview__body article strong {
  display: block;
  margin-top: 4px;
}
.theme-live-preview__body p {
  margin: 5px 0 10px;
  color: var(--theme-muted);
  font-size: 12px;
  line-height: 1.55;
}
.theme-live-preview__body article div {
  display: flex;
  gap: 8px;
}
.theme-live-preview__body button,
.theme-live-preview__body input {
  min-height: 32px;
  border: 1px solid var(--theme-control-border);
  border-radius: calc(var(--theme-radius) * 0.7);
  color: var(--theme-text);
  background: var(--theme-control);
  font: inherit;
}
.theme-live-preview__body button { padding: 0 10px; }
.theme-live-preview__body button.primary { border-color: var(--theme-primary); color: var(--theme-primary); }
.theme-live-preview__body label { display: grid; gap: 5px; }
.theme-live-preview__body input { width: 100%; padding: 0 10px; }
.theme-cover-review { position: relative; min-height: 180px; margin: 0; overflow: hidden; border-radius: var(--aw-radius-sm); background: var(--aw-bg-muted); }
.theme-cover-review img { display: block; width: 100%; height: 100%; min-height: 180px; object-fit: cover; }.theme-cover-review > div { display: grid; min-height: 180px; place-items: center; color: var(--aw-text-secondary); }.theme-cover-review figcaption { position: absolute; right: 9px; bottom: 9px; padding: 3px 7px; border-radius: 999px; color: #fff; background: rgb(0 0 0 / 48%); font-size: 12px; }.theme-screenshot-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; }.theme-screenshot-grid :deep(.ant-image), .theme-screenshot-grid :deep(.ant-image-img) { display: block; width: 100%; }.theme-screenshot-grid :deep(.ant-image-img) { height: 150px; object-fit: cover; border-radius: var(--aw-radius-sm); }
pre {
  max-height: 320px;
  margin: 10px 0 0;
  padding: 12px;
  overflow: auto;
  border-radius: var(--aw-radius-sm);
  background: var(--aw-bg-muted);
  font:
    12px/1.55 ui-monospace,
    SFMono-Regular,
    Menlo,
    monospace;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}
.agent-summary p {
  margin: 10px 0 0;
  line-height: 1.65;
}
.agent-summary ul {
  margin: 10px 0 0;
  padding-left: 20px;
}
.test-form {
  display: grid;
  grid-template-columns: 1fr 180px;
  gap: 12px;
}
.test-record p {
  margin: 8px 0;
  white-space: pre-wrap;
}
.test-record small {
  font-size: 12px;
}
@media (max-width: 700px) {
  .review-head {
    display: grid;
  }
  .facts,
  .test-form,
  .theme-review-grid {
    grid-template-columns: 1fr;
  }
}
</style>
